import { Quaternion, Vector3 } from "three";
import { SugarModel } from "../../../loader/SugarModel";
import { SugarModelConfigurator } from "../../../loader/SugarModelConfigurator";
import { SugarModelJoint } from "../../../loader/SugarModelJoint";
import { Room } from "../../../shape/Scene";
import { getConfiguratorComposition } from "../../configurator/ConfiguratorCompositionRegistry";
import {
  applyJointSnapDelta,
  computeSnapTailToBaseDelta,
  fetchMinDistance,
} from "../../../util/JointUtil";
import type { DragSessionConstraint } from "../DragSessionPort";

export const DESIGNER_JOINT_SNAP_COMMIT = "designer-joint-snap-commit";
export const DESIGNER_CONFIGURATOR_MODEL_DETACHED =
  "designer-configurator-model-detached";

export type DesignerJointSnapCommitDetail = {
  model: SugarModel;
  baseJoint: SugarModelJoint;
  tailJoint: SugarModelJoint;
};

export type DesignerConfiguratorModelDetachedDetail = {
  model: SugarModel;
  configurator: SugarModelConfigurator;
  removedConfigurator: boolean;
};

/**
 * The single joint-snap implementation for both products. The configurator
 * drives it through `DesignerTransformer.allowConfiguratorRootSnap = false`
 * (piece-only snap, no config↔config merge); Room Designer leaves it true.
 * A separate `ConfiguratorJointSnapConstraint` used to exist but had no
 * callers — it was deleted rather than kept in sync.
 */
export class JointSnapConstraint implements DragSessionConstraint {
  private _otherModels: SugarModel[] = [];
  private _snappedJoints?: { base: SugarModelJoint; tail: SugarModelJoint };
  /** Leftover after middle-piece detach — complete() only on drop (onDragEnd). */
  private _pendingCompleteConfigurator?: SugarModelConfigurator;
  /**
   * Intact configurator root ↔ peer snap/merge.
   * RD: true. CFG: false (piece-only snap; no config↔config merge).
   */
  private _allowConfiguratorRootSnap = true;

  constructor(
    private _scene: Room,
    /** World-unit joint capture radius. RD default 0.6 (60cm); CFG overrides (1.2). */
    private _snapDistance = 0.6,
  ) {}

  set allowConfiguratorRootSnap(value: boolean) {
    this._allowConfiguratorRootSnap = value;
  }

  get allowConfiguratorRootSnap() {
    return this._allowConfiguratorRootSnap;
  }

  set snapDistance(value: number) {
    this._snapDistance = value;
  }

  get snapDistance() {
    return this._snapDistance;
  }

  onDragStart(model: SugarModel): void {
    this._pendingCompleteConfigurator = undefined;
    this._detachFromConfiguratorIfNeeded(model);
    this._otherModels = this._scene.models.filter(
      (m): m is SugarModel => m instanceof SugarModel && m !== model,
    );
    this._snappedJoints = undefined;
  }

  private _detachFromConfiguratorIfNeeded(model: SugarModel): void {
    if (model instanceof SugarModelConfigurator) return;

    const configurator = model.configurator;
    if (!(configurator instanceof SugarModelConfigurator)) return;

    const worldPosition = new Vector3();
    const worldQuaternion = new Quaternion();
    model.updateMatrixWorld(true);
    model.getWorldPosition(worldPosition);
    model.getWorldQuaternion(worldQuaternion);

    const composition = getConfiguratorComposition(this._scene);
    if (composition) {
      // RD host addRoot — default addAsRoot=true. CFG already detach(false) in beforeGrab.
      composition.detach(model);
    } else {
      configurator.grabModel(model);
      model.configurator = undefined;
      model.detach();
    }
    model.position.copy(worldPosition);
    model.quaternion.copy(worldQuaternion);
    model.updateMatrixWorld(true);

    if (!this._scene.models.includes(model)) {
      this._scene.addModel(model);
    }

    let removedConfigurator = false;
    if (configurator.models.length < 1) {
      configurator.clearSideParts(true);
      this._scene.removeModel(configurator);
      configurator.removeFromParent();
      removedConfigurator = true;
    } else if (composition?.dissolveIfSingle(configurator)) {
      // İki parçadan biri çekildi: kalan parça da bağımsız olur, configurator kalmaz.
      removedConfigurator = true;
    } else {
      // Defer complete until drop so user can confirm the split.
      this._pendingCompleteConfigurator = configurator;
    }

    this._scene.dispatchEvent(
      new CustomEvent<DesignerConfiguratorModelDetachedDetail>(
        DESIGNER_CONFIGURATOR_MODEL_DETACHED,
        {
          detail: {
            model,
            configurator,
            removedConfigurator,
          },
        },
      ),
    );
  }

  apply(model: SugarModel, position: Vector3): Vector3 {
    model.position.copy(position);
    model.updateMatrixWorld(true);

    // CFG: intact root has no joint snap. RD: roots may merge with peers.
    if (
      model instanceof SugarModelConfigurator &&
      !this._allowConfiguratorRootSnap
    ) {
      this._snappedJoints = undefined;
      return position;
    }

    if (this._snappedJoints) {
      const delta = computeSnapTailToBaseDelta(
        this._snappedJoints.base,
        this._snappedJoints.tail,
      );
      if (
        delta &&
        delta.translation.length() < this._snapDistance * 1.2
      ) {
        applyJointSnapDelta(model, delta);
        return model.position.clone();
      }
      this._snappedJoints = undefined;
    }

    const min = fetchMinDistance(model, this._otherModels);

    if (min && min.distance < this._snapDistance) {
      this._alignDraggedModel(model, min.base, min.tail);
      this._snappedJoints = { base: min.base, tail: min.tail };
      return model.position.clone();
    }

    this._snappedJoints = undefined;
    return position;
  }

  onDragEnd(model: SugarModel): void {
    if (this._snappedJoints) {
      this._alignDraggedModel(
        model,
        this._snappedJoints.base,
        this._snappedJoints.tail,
      );
      this._scene.dispatchEvent(
        new CustomEvent<DesignerJointSnapCommitDetail>(
          DESIGNER_JOINT_SNAP_COMMIT,
          {
            detail: {
              model,
              baseJoint: this._snappedJoints.base,
              tailJoint: this._snappedJoints.tail,
            },
          },
        ),
      );
    }

    const leftover = this._pendingCompleteConfigurator;
    if (leftover && leftover.models.length > 0) {
      leftover.complete();
    }
    this._pendingCompleteConfigurator = undefined;

    this._snappedJoints = undefined;
    this._otherModels = [];
  }

  get snappedJoints() {
    return this._snappedJoints;
  }

  private _alignDraggedModel(
    model: SugarModel,
    base: SugarModelJoint,
    tail: SugarModelJoint,
  ): void {
    const delta = computeSnapTailToBaseDelta(base, tail);
    if (!delta) return;
    applyJointSnapDelta(model, delta);
  }
}
